import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import TriggerPanel from './TriggerPanel';
import { TRIGGERS } from './triggers';

/** Un posto solo per le comunicazioni automatiche: una scheda per tipologia. */
const AutomationHub = ({ automations, onRefresh, waConnected }) => {
  const { API, getAuthHeaders } = useAuth();
  const [tab, setTab] = useState(TRIGGERS[0].id);
  const [presets, setPresets] = useState([]);
  const [reviewUrl, setReviewUrl] = useState('');
  const [history, setHistory] = useState([]);
  const [autoSettings, setAutoSettings] = useState(null);
  const [waHealth, setWaHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingAuto, setSavingAuto] = useState(false);

  const load = useCallback(async () => {
    try {
      const [presetsRes, historyRes, autoRes, healthRes] = await Promise.all([
        axios.get(`${API}/marketing/whatsapp/campaign-presets`, getAuthHeaders()),
        axios.get(`${API}/marketing/whatsapp/campaign/history`, getAuthHeaders()),
        axios.get(`${API}/marketing/whatsapp/auto-settings`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/health`, getAuthHeaders()).catch((e) => ({ data: { status: 'error', error_message: e.message } })),
      ]);
      setPresets(presetsRes.data.presets || []);
      setReviewUrl(presetsRes.data.review_url || '');
      setHistory(historyRes.data.items || []);
      setAutoSettings(autoRes.data);
      setWaHealth(healthRes.data);
    } catch (err) {
      console.error('Errore caricamento automazioni:', err);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const saveAuto = async (override) => {
    const payload = override ? { ...autoSettings, ...override } : autoSettings;
    if (override) setAutoSettings(payload);
    setSavingAuto(true);
    try {
      await axios.put(`${API}/marketing/whatsapp/auto-settings`, payload, getAuthHeaders());
      toast.success('Impostazioni salvate');
    } catch (err) {
      toast.error('Salvataggio non riuscito', { description: err.response?.data?.detail || err.message });
      if (override) setAutoSettings(autoSettings);
    } finally {
      setSavingAuto(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  // WhatsApp può essere collegato come account social oppure configurato lato server
  const waUsable = waConnected || waHealth?.status === 'ok' || waHealth?.status === 'expired';

  return (
    <div className="space-y-5" data-testid="automation-hub">
      {!waUsable && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            Collega WhatsApp Business in <strong>Impostazioni → Social</strong> per inviare su WhatsApp.
            Email e SMS funzionano comunque.
          </p>
        </div>
      )}
      {waHealth?.status === 'expired' && (
        <div className="flex items-start gap-3 p-4 rounded-lg border-2 border-destructive bg-destructive/10" data-testid="wa-health-expired">
          <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-destructive">Collegamento WhatsApp scaduto: gli invii sono fermi</p>
            <p className="text-sm text-destructive/90 mt-1">{waHealth.error_message}</p>
            <p className="text-xs text-foreground/80 mt-2">{waHealth.instructions}</p>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-5">
        <div className="lg:hidden">
          <Select value={tab} onValueChange={setTab}>
            <SelectTrigger className="w-full min-h-[48px]" data-testid="automation-tab-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRIGGERS.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`automation-option-${t.id}`}>
                  <span className="flex items-center gap-2"><t.icon className={`w-4 h-4 ${t.color}`} />{t.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsList className="hidden lg:inline-flex w-auto justify-start gap-1 h-auto p-1" data-testid="automation-tabs">
          {TRIGGERS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} data-testid={`automation-tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-2" />{t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {TRIGGERS.map((t) => {
          const preset = presets.find((p) => p.id === t.presetId);
          return (
            <TabsContent key={t.id} value={t.id}>
              <TriggerPanel
                trigger={t}
                automations={automations.filter((a) => a.trigger === t.id)}
                onRefresh={onRefresh}
                preset={preset}
                reviewUrl={reviewUrl}
                waConnected={waUsable}
                autoSettings={autoSettings}
                onAutoField={(k, v) => setAutoSettings((s) => ({ ...s, [k]: v }))}
                onSaveAuto={saveAuto}
                savingAuto={savingAuto}
                history={preset ? history.filter((h) => h.template_name === preset.template_name) : []}
                onCampaignSent={load}
              />
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

export default AutomationHub;
